import { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";

import { GeoJSONSource, Map } from "maplibre-gl";
import { FeatureCollection, GeoJsonProperties, Geometry } from "geojson";

import {
  DEFAULT_STEP,
  SPATIAL_ANALYSIS_RESULT_DATASET_NAME,
  SPATIAL_TYPE_TO_LAYER_TYPE
} from "@/constants/dataset";
import { SUBLAYERS } from "@/constants/map";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { updateDatasetStyle } from "@/store/slice/datasetSlice";
import { SpatialAggregationType } from "@/types/common";
import {
  DataSchemaType,
  Dataset,
  DatasetAttributeSchema,
  DatasetStyle,
  EColorLabel,
  EColorNames
} from "@/types/dataset";
import {
  generateColorRanges,
  handleCategoricalValues,
  handleNumericValues
} from "@/utils/common";
import { isOrdinalType } from "@/utils/dataset";
import {
  calculateRankOffset,
  changeCategoryLabelLayer,
  changeColorAutoRangeLayer,
  changeColorCategoryLayer,
  changeLabelLayerColor,
  changeLabelLayerOpacity,
  changeLabelLayerVisibility,
  changeOpacityLayer,
  getSourceProperties,
  waitForSource
} from "@/utils/map";
import { mergeRankToFeatures } from "@/utils/map/rank";

/**
 * Hook for map layers style management
 */
export const useMapStyles = (map: Map) => {
  const dispatch = useDispatch();
  const { isRankingLabelEnabled } = useAppFeatures();
  const colorUpdateGenRef = useRef<Record<string, number>>({});

  const prepareLayerStyles = useCallback(
    (
      polygonData:
        | FeatureCollection<Geometry>
        | FeatureCollection<Geometry, GeoJsonProperties>,
      dataset: Dataset,
      selectedCategory: DatasetAttributeSchema,
      step: number
    ) => {
      // Extract property values
      const propertyValues = polygonData.features.map(
        (f) => f.properties?.[selectedCategory.attribute_name]
      );

      const isCategory = isOrdinalType(selectedCategory.data_type);

      const ranges = isCategory
        ? handleCategoricalValues(propertyValues, step)
        : handleNumericValues(propertyValues, step);

      const colorRanges = generateColorRanges(
        ranges,
        dataset.style.colorFamily,
        selectedCategory.data_type,
        step
      );

      const isSpatialAnalysisResultDataset: boolean =
        dataset.data.name === SPATIAL_ANALYSIS_RESULT_DATASET_NAME;

      const updatedStyle: DatasetStyle = {
        ...(dataset.style || {}),
        colorRanges,
        colorFamily: dataset.style.colorFamily,
        colorSteps: step,
        rankConfig: {
          isEnabled:
            (isSpatialAnalysisResultDataset ||
              dataset.style?.rankConfig?.isEnabled) ??
            false,
          maxRanking: Math.min(
            dataset.style?.rankConfig?.maxRanking ?? 1,
            polygonData.features.length
          )
        }
      };
      dispatch(
        updateDatasetStyle({ datasetId: dataset.data.id, style: updatedStyle })
      );

      return { updatedStyle };
    },
    [dispatch]
  );

  const handleChangeOpacityLayers = (
    datasetId: string,
    opacity: number,
    spatialType: SpatialAggregationType
  ) => {
    const layerType = SPATIAL_TYPE_TO_LAYER_TYPE[spatialType];
    const sublayers = SUBLAYERS(datasetId);

    changeOpacityLayer(map, sublayers[layerType], opacity);
  };

  const handleChangeColorDataLayers = (
    dataset: Dataset,
    selectedCategoryMetadata: DatasetAttributeSchema,
    colorFamily: EColorNames,
    step: number = DEFAULT_STEP,
    suffix?: string
  ) => {
    const sourceId = suffix ? `${dataset.data.id}-${suffix}` : dataset.data.id;
    const source = getSourceProperties(map, sourceId);

    if (!source) return;

    const { updatedStyle } = prepareLayerStyles(
      source as FeatureCollection<Geometry, GeoJsonProperties>,
      dataset,
      selectedCategoryMetadata,
      step
    );

    handleChangeColorLayers(
      dataset.data.id,
      colorFamily,
      updatedStyle.colorRanges,
      selectedCategoryMetadata.attribute_name,
      dataset.properties.spatialType,
      selectedCategoryMetadata.data_type,
      step
    );
  };

  // TODO: Tech debt might duplicate with @toggleLayerVisibilityById src/hooks/map/styling/useMapStyles.ts:134
  const handleChangeVisibility = (
    datasetId: string,
    visibility: "visible" | "none",
    spatialType: SpatialAggregationType,
    showLabel: boolean,
    isRankEnabled: boolean
  ) => {
    const layer = SUBLAYERS(datasetId)[spatialType];

    Object.values(layer).forEach((layerId) => {
      switch (layerId) {
        case layerId.includes("label") && layerId:
          changeLabelLayerVisibility(map, layerId, showLabel);
          break;
        case layerId.includes("rank") && layerId:
          handleStyleRankEnabledChange({
            datasetId,
            isEnabled: isRankEnabled,
            isShowLabel: showLabel
          });
          break;
        default:
          map.setLayoutProperty(layerId, "visibility", visibility);
      }
    });
  };

  const handleFilterLayers = (datasetId: string) => {
    if (!map) return;

    const style = map.getStyle();
    if (!style?.layers) return;

    style.layers.forEach((layer) => {
      if (
        layer.id.includes("-fill") ||
        layer.id.includes("-outline") ||
        layer.id.includes("-circle") ||
        layer.id.includes("-count") ||
        layer.id.includes("-unclustered-points") ||
        layer.id.includes("-label")
      ) {
        const isTargetDataset = layer.id.startsWith(`${datasetId}-`);
        map.setLayoutProperty(
          layer.id,
          "visibility",
          isTargetDataset ? "visible" : "none"
        );
      }
    });
  };

  const handleChangeColorLayers = useCallback(
    (
      datasetId: string,
      colorFamily: EColorNames,
      colorRanges: DatasetStyle["colorRanges"],
      selectedCategoryName: string,
      spatialType: SpatialAggregationType,
      dataType: DataSchemaType,
      step: number = 10
    ) => {
      const layerType = SPATIAL_TYPE_TO_LAYER_TYPE[spatialType];
      const sublayers = SUBLAYERS(datasetId);

      if (isOrdinalType(dataType)) {
        changeColorCategoryLayer(
          map,
          sublayers[layerType],
          colorFamily,
          colorRanges,
          selectedCategoryName,
          step
        );
      } else {
        changeColorAutoRangeLayer(
          map,
          sublayers[layerType],
          colorFamily,
          colorRanges,
          selectedCategoryName,
          step
        );
      }
    },
    [map]
  );

  const handleChangeVisibleLabelLayer = (
    layerId: string,
    visibleLabel: boolean
  ) => {
    changeLabelLayerVisibility(map, layerId, visibleLabel);
  };

  const handleDisplayNameLabelLayerCategory = (
    layerId: string,
    selectedCategory: DatasetAttributeSchema
  ) => {
    changeCategoryLabelLayer(map, layerId, selectedCategory);
  };

  const handleChangeLabelLayerColor = (
    layerId: string,
    selectedColor: EColorLabel
  ) => {
    changeLabelLayerColor(map, layerId, selectedColor);
  };

  const handleChangeLabelLayerOpacity = (layerId: string, opacity: number) => {
    changeLabelLayerOpacity(map, layerId, opacity);
  };

  const handleStyleRankEnabledChange = ({
    datasetId,
    isEnabled,
    isShowLabel
  }: {
    datasetId: string;
    isEnabled: boolean;
    isShowLabel: boolean;
  }) => {
    if (!map) return;

    const style = map.getStyle();
    if (!style?.layers) return;

    const labelLayerId = `${datasetId}-label`;
    const rankLayerId = `${datasetId}-rank`;

    // Update rank layer visibility
    map.setLayoutProperty(
      rankLayerId,
      "visibility",
      isEnabled ? "visible" : "none"
    );

    // Calculate and apply the new offset based on label visibility
    const rankLayer = style.layers.find((l) => l.id === rankLayerId);
    if (rankLayer) {
      const rankOffset = calculateRankOffset(map, labelLayerId, isShowLabel);
      map.setLayoutProperty(rankLayerId, "text-offset", rankOffset);
    }
  };

  const handleUpdateColorRangesByTimeSeries = useCallback(
    async (dataset: Dataset, xAxisKey: string, currentTime: string) => {
      const datasetId = dataset.data.id;
      const gen = (colorUpdateGenRef.current[datasetId] =
        (colorUpdateGenRef.current[datasetId] ?? 0) + 1);
      await waitForSource(map, datasetId);

      // Discard stale calls — a newer invocation for this dataset has already taken over
      if (gen !== colorUpdateGenRef.current[datasetId]) return;

      const source = getSourceProperties(map, datasetId);
      if (!source) return;

      const featureCollection = source as FeatureCollection<
        Geometry,
        GeoJsonProperties
      >;

      const isNumber = currentTime !== "" && !Number.isNaN(Number(currentTime));
      const parsedTime = isNumber ? Number(currentTime) : currentTime;

      const filteredFeatureCollection = {
        ...featureCollection,
        features: featureCollection.features.filter(
          (f) => f.properties?.[xAxisKey] === parsedTime
        )
      };

      if (filteredFeatureCollection.features.length === 0) return;

      const step = dataset.style.colorSteps ?? DEFAULT_STEP;
      const { updatedStyle } = prepareLayerStyles(
        filteredFeatureCollection,
        dataset,
        dataset.selectedCategory,
        step
      );
      handleChangeColorLayers(
        dataset.data.id,
        dataset.style.colorFamily,
        updatedStyle.colorRanges,
        dataset.selectedCategory.attribute_name,
        dataset.properties.spatialType,
        dataset.selectedCategory.data_type,
        step
      );
    },
    [handleChangeColorLayers, map, prepareLayerStyles]
  );

  const handleUpdateSourceWithRank = async ({
    datasetId,
    style,
    update,
    sourceData
  }: {
    datasetId: string;
    style: DatasetStyle;
    update: {
      isEnabled?: boolean;
      maxRanking?: number;
      category?: DatasetAttributeSchema;
    };
    /**
     * Reuses the collection already available to avoid the expensive
     * `source.getData()` worker round-trip. Omit when unavailable.
     */
    sourceData?: FeatureCollection;
  }) => {
    if (!map) return;

    const sourceId = map.getSource(`${datasetId}-centroid`)
      ? `${datasetId}-centroid`
      : datasetId;

    const source = map.getSource<GeoJSONSource>(sourceId);

    if (!source) return;

    const featureCollection =
      sourceData ?? ((await source.getData()) as FeatureCollection);

    const updatedStyle: DatasetStyle = {
      ...style,
      labelConfig: {
        ...style.labelConfig,
        attribute_id: update?.category?.id ?? style.labelConfig.attribute_id,
        attribute_name:
          update?.category?.attribute_name ?? style.labelConfig.attribute_name
      },
      rankConfig: {
        ...style.rankConfig,
        isEnabled: update.isEnabled ?? style.rankConfig?.isEnabled ?? false,
        maxRanking: update.maxRanking ?? style.rankConfig?.maxRanking ?? 10
      }
    };

    // Copy: with `sourceData` this is the caller's own object, not a throwaway
    // from `getData()`.
    source.setData({
      ...featureCollection,
      features: mergeRankToFeatures(
        isRankingLabelEnabled,
        featureCollection.features,
        updatedStyle
      )
    });
  };

  return {
    prepareLayerStyles,
    handleChangeOpacityLayers,
    handleChangeColorDataLayers,
    handleChangeColorLayers,
    handleFilterLayers,
    handleChangeVisibility,
    handleChangeVisibleLabelLayer,
    handleDisplayNameLabelLayerCategory,
    handleChangeLabelLayerColor,
    handleChangeLabelLayerOpacity,
    handleStyleRankEnabledChange,
    handleUpdateSourceWithRank,
    handleUpdateColorRangesByTimeSeries
  };
};

export default useMapStyles;
